import type { AxiosInstance } from 'axios';
import type {
  Campaign,
  CampaignPaginatedResponse,
  CampaignQueryParams,
  CreateCampaignRequest,
  PatchCampaignRequest,
  CampaignUser,
} from './contracts/campaign';
import type {
  CampaignContactPaginatedResponse,
  CampaignContactQueryParams,
  CampaignContactResponse,
  CreateCampaignContactRequest,
  PatchCampaignContactRequest,
} from './contracts/campaign-contacts';
import type {
  CampaignContactsDashboardData,
  CampaignContactsDashboardQueryParams,
} from './contracts/campaign-contacts-dashboard';
import type {
  CampaignWorkGroup,
  CampaignWorkGroupPaginatedResponse,
  CampaignWorkGroupQueryParams,
  CreateCampaignWorkGroupRequest,
} from './contracts/work-group';
import type { CampaignAccessResponse } from './contracts/access';
import type {
  CampaignCompanyPaginatedResponse,
  CampaignCompanyQueryParams,
  CampaignCompanyResponse,
  EnableCampaignCompanyRequest,
} from './contracts/campaign-company';

/**
 * Gateway for interacting with the Campaigns API.
 */
export class CampaignsGateway {
  constructor(
    private readonly httpClient: AxiosInstance,
    private readonly baseUrl: string,
  ) {}

  /**
   * Retrieves a paginated list of campaigns.
   * @param query - Optional query parameters for filtering and pagination.
   * @returns The paginated campaigns response.
   */
  async find(query?: CampaignQueryParams): Promise<CampaignPaginatedResponse> {
    const { data } = await this.httpClient.get<CampaignPaginatedResponse>(`${this.baseUrl}/campaigns`, {
      params: query,
    });
    return data;
  }

  /**
   * Retrieves a campaign by its unique identifier.
   * @param id - The unique identifier of the campaign.
   * @returns The campaign data.
   */
  async get(id: string): Promise<Campaign> {
    const { data } = await this.httpClient.get<Campaign>(`${this.baseUrl}/campaigns/${id}`);
    return data;
  }

  /**
   * Creates a new campaign.
   * @param payload - The campaign data to create.
   * @returns The created campaign.
   */
  async create(payload: CreateCampaignRequest): Promise<Campaign> {
    const { data } = await this.httpClient.post<Campaign>(`${this.baseUrl}/campaigns`, payload);
    return data;
  }

  /**
   * Partially updates an existing campaign.
   * @param id - The unique identifier of the campaign.
   * @param payload - The fields to update.
   * @returns The updated campaign.
   */
  async patch(id: string, payload: PatchCampaignRequest): Promise<Campaign> {
    const { data } = await this.httpClient.patch<Campaign>(`${this.baseUrl}/campaigns/${id}`, payload);
    return data;
  }

  /**
   * Removes a campaign by its unique identifier.
   * @param id - The unique identifier of the campaign.
   * @returns The removed campaign.
   */
  async remove(id: string): Promise<Campaign> {
    const { data } = await this.httpClient.delete<Campaign>(`${this.baseUrl}/campaigns/${id}`);
    return data;
  }

  /**
   * Retrieves a paginated list of campaign contacts.
   * @param query - Optional query parameters for filtering and pagination.
   * @returns The paginated campaign contacts response.
   */
  async findCampaignContacts(query?: CampaignContactQueryParams): Promise<CampaignContactPaginatedResponse> {
    const { data } = await this.httpClient.get<CampaignContactPaginatedResponse>(`${this.baseUrl}/campaign-contacts`, {
      params: query,
    });
    return data;
  }

  /**
   * Retrieves a campaign contact by its unique identifier.
   * @param id - The unique identifier of the campaign contact.
   * @returns The campaign contact data.
   */
  async getCampaignContacts(id: string): Promise<CampaignContactResponse> {
    const { data } = await this.httpClient.get<CampaignContactResponse>(`${this.baseUrl}/campaign-contacts/${id}`);
    return data;
  }

  /**
   * Creates a new campaign contact.
   * @param payload - The campaign contact data to create.
   * @returns The created campaign contact.
   */
  async createCampaignContact(payload: CreateCampaignContactRequest): Promise<CampaignContactResponse> {
    const { data } = await this.httpClient.post<CampaignContactResponse>(`${this.baseUrl}/campaign-contacts`, payload);
    return data;
  }

  /**
   * Adds multiple contacts to a campaign in a single request.
   * @param payload - The list of campaign contacts to create.
   * @returns The created campaign contacts.
   */
  async addContactsToCampaign(payload: CreateCampaignContactRequest[]): Promise<CampaignContactResponse[]> {
    const { data } = await this.httpClient.post<CampaignContactResponse[]>(
      `${this.baseUrl}/campaign-contacts`,
      payload,
    );
    return data;
  }

  /**
   * Partially updates an existing campaign contact.
   * @param id - The unique identifier of the campaign contact.
   * @param payload - The fields to update.
   * @returns The updated campaign contact.
   */
  async patchCampaignContact(id: string, payload: PatchCampaignContactRequest): Promise<CampaignContactResponse> {
    const { data } = await this.httpClient.patch<CampaignContactResponse>(
      `${this.baseUrl}/campaign-contacts/${id}`,
      payload,
    );
    return data;
  }

  /**
   * Removes a campaign contact by its unique identifier.
   * @param id - The unique identifier of the campaign contact.
   * @returns The removed campaign contact.
   */
  async removeCampaignContact(id: string): Promise<CampaignContactResponse> {
    const { data } = await this.httpClient.delete<CampaignContactResponse>(`${this.baseUrl}/campaign-contacts/${id}`);
    return data;
  }

  async getContactsDashboardData(
    query: CampaignContactsDashboardQueryParams,
  ): Promise<CampaignContactsDashboardData> {
    const { data } = await this.httpClient.get<CampaignContactsDashboardData>(
      `${this.baseUrl}/campaign-contacts-dashboard`,
      {
        params: query,
      },
    );
    return data;
  }

  async getUsers() {
    const { data } = await this.httpClient.get<CampaignUser[]>(`${this.baseUrl}/users`);
    return data;
  }

  async listWorkGroups(query?: CampaignWorkGroupQueryParams): Promise<CampaignWorkGroupPaginatedResponse> {
    const { data } = await this.httpClient.get<CampaignWorkGroupPaginatedResponse>(`${this.baseUrl}/work-groups`, {
      params: query,
    });
    return data;
  }

  async createWorkGroup(payload: CreateCampaignWorkGroupRequest): Promise<CampaignWorkGroup> {
    const { data } = await this.httpClient.post<CampaignWorkGroup>(`${this.baseUrl}/work-groups`, payload);
    return data;
  }

  async deleteWorkGroup(id: string): Promise<CampaignWorkGroup> {
    const { data } = await this.httpClient.delete<CampaignWorkGroup>(`${this.baseUrl}/work-groups/${id}`);
    return data;
  }

  /**
   * Lists company installations of the Campaigns module.
   * @param query - Optional query parameters (e.g. companyId, enabled).
   * @returns The paginated list of campaign company installations.
   */
  async findCampaignCompanies(query?: CampaignCompanyQueryParams): Promise<CampaignCompanyPaginatedResponse> {
    const { data } = await this.httpClient.get<CampaignCompanyPaginatedResponse>(
      `${this.baseUrl}/campaign-companies`,
      { params: query },
    );
    return data;
  }

  /**
   * Retrieves the Campaigns module installation for a given company.
   * @param companyId - The company identifier.
   * @returns The campaign company installation, or null when the module is not installed.
   */
  async getCampaignCompany(companyId: string): Promise<CampaignCompanyResponse | null> {
    try {
      const { data } = await this.httpClient.get<CampaignCompanyResponse>(
        `${this.baseUrl}/campaign-companies/${companyId}`,
      );
      return data;
    } catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Enables the Campaigns module for a given company.
   * @param payload - The enable request payload (companyId, optional enabledByUserId).
   * @returns The created campaign company installation.
   */
  async enableCampaignCompany(payload: EnableCampaignCompanyRequest): Promise<CampaignCompanyResponse> {
    const { data } = await this.httpClient.post<CampaignCompanyResponse>(
      `${this.baseUrl}/campaign-companies`,
      payload,
    );
    return data;
  }

  /**
   * Disables the Campaigns module for a given company by removing the installation record.
   * @param id - The campaign company installation id.
   * @returns The removed campaign company installation.
   */
  async disableCampaignCompany(id: string): Promise<CampaignCompanyResponse> {
    const { data } = await this.httpClient.delete<CampaignCompanyResponse>(
      `${this.baseUrl}/campaign-companies/${id}`,
    );
    return data;
  }

  /**
   * Checks whether the current caller has access to the Campaigns module.
   * @returns The access verdict for the authenticated user.
   */
  async getMyAccess(): Promise<CampaignAccessResponse> {
    const { data } = await this.httpClient.get<CampaignAccessResponse>(
      `${this.baseUrl}/campaigns-access`,
    );
    return data;
  }
}

export type {
  Campaign,
  CampaignPaginatedResponse,
  CampaignQueryParams,
  CampaignRetryConfig,
  CampaignStatus,
  CampaignProvider,
  WhatsAppPayload,
  WhatsAppTemplateComponent,
  CampaignContactFilter,
  CampaignSchedule,
  CreateCampaignRequest,
  PatchCampaignRequest,
  CampaignUser,
} from './contracts/campaign';
export type {
  CampaignContactCampaignStatus,
  CampaignContactDispatchStatus,
  CampaignContactLikeQueryOperators,
  CampaignContactPaginatedResponse,
  CampaignContactQueryOperators,
  CampaignContactQueryParams,
  CampaignContactQueryValue,
  CampaignContactResponse,
  CampaignContactSortDirection,
  CampaignContactSortParams,
  CreateCampaignContactRequest,
  PatchCampaignContactRequest,
} from './contracts/campaign-contacts';
export type {
  CampaignContactsDashboardChannelPerformance,
  CampaignContactsDashboardData,
  CampaignContactsDashboardExactQueryOperators,
  CampaignContactsDashboardEvolutionItem,
  CampaignContactsDashboardFunnelItem,
  CampaignContactsDashboardInterval,
  CampaignContactsDashboardMetrics,
  CampaignContactsDashboardQueryParams,
  CampaignContactsDashboardQueryValue,
  CampaignContactsDashboardRangeQueryOperators,
  CampaignContactsDashboardStatusDistribution,
  CampaignContactsDashboardStatusMetrics,
  CampaignContactsDashboardSummary,
  CampaignContactsDashboardTopCampaign,
} from './contracts/campaign-contacts-dashboard';
export type {
  CampaignWorkGroup,
  CampaignWorkGroupPaginatedResponse,
  CampaignWorkGroupQueryParams,
  CreateCampaignWorkGroupRequest,
} from './contracts/work-group';
export type { CampaignAccessResponse, CampaignAccessReason } from './contracts/access';
export type {
  CampaignCompanyPaginatedResponse,
  CampaignCompanyQueryParams,
  CampaignCompanyResponse,
  EnableCampaignCompanyRequest,
} from './contracts/campaign-company';
